import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useData } from "../../context/DataContext";
import { useAuth } from "../../context/AuthContext";
import { useHierarchy } from "../../hooks/useHierarchy";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Badge } from "../ui/badge";
import { QualificationStatusBadge } from "./QualificationStatusBadge";
import { calculateQualificationStatus, formatDate, getDaysUntilExpiry } from "../../utils/qualification-utils";
import { Qualification } from "../../types";
import { Trash2, Edit, Paperclip, Search, Eye } from "lucide-react";
import { Input } from "../ui/input";
import { Can } from "../Can";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import { DeleteQualificationDialog } from "./DeleteQualificationDialog";
import { QualificationDrawer } from "./QualificationDrawer";
import { toast } from "sonner";

interface QualificationsListViewProps {
  qualifications: Qualification[];
  showStaffColumn?: boolean;
  isReadOnly?: boolean;
}

interface EnrichedQualification extends Qualification {
  calculatedStatus: "Active" | "Expiring Soon" | "Expired";
  daysUntilExpiry: number | null;
}

export const QualificationsListView: React.FC<QualificationsListViewProps> = ({ 
  qualifications: allQualifications,
  showStaffColumn = true,
  isReadOnly = false
}) => {
  const { deleteQualification } = useData();
  const { currentUser } = useAuth();
  const { canManageUser } = useHierarchy();
  const navigate = useNavigate();
  
  const [searchTerm, setSearchTerm] = useState("");
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [qualificationToDelete, setQualificationToDelete] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerQualification, setDrawerQualification] = useState<Qualification | null>(null);

  // Apply search filter
  const filteredQualifications = useMemo(() => {
    return (allQualifications || []).filter((q: Qualification) => {
        const matchesName = (q.name || "").toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStaff = (q.person?.name || "").toLowerCase().includes(searchTerm.toLowerCase());
        return matchesName || matchesStaff;
    });
  }, [allQualifications, searchTerm]);

  // Enrich qualifications with calculated status
  const enrichedQualifications = useMemo<EnrichedQualification[]>(() => {
    return (filteredQualifications || []).map((q: Qualification) => ({
      ...q,
      calculatedStatus: calculateQualificationStatus(q),
      daysUntilExpiry: getDaysUntilExpiry(q.expiryDate)
    }));
  }, [filteredQualifications]);

  const handleEditQualification = (qualification: Qualification) => {
    navigate(`/people/${qualification.person?.id}/qualifications/edit/${qualification.id}`);
  };
  
  const confirmDelete = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setQualificationToDelete(id);
    setIsDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (qualificationToDelete) {
      await deleteQualification(qualificationToDelete);
      setIsDeleteDialogOpen(false);
      setQualificationToDelete(null);
      toast.success("Qualification moved to Recycle Bin");
    }
  };
  
  const handleViewDetails = (qualification: Qualification) => {
    setDrawerQualification(qualification);
    setIsDrawerOpen(true);
  };

  return (
    <div className="space-y-4">
      {/* Search Bar */}
      <div className="flex items-center justify-between">
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input 
            placeholder="Search qualifications..." 
            className="pl-9 h-10 bg-white border-slate-200 focus-visible:ring-slate-200 shadow-sm" 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Table Card */}
      <Card className="border-slate-100 shadow-sm overflow-hidden">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/50 hover:bg-slate-50/50 border-slate-100">
                  {showStaffColumn && <TableHead className="py-4 text-xs font-bold text-slate-900">Staff</TableHead>}
                  <TableHead className="py-4 text-xs font-bold text-slate-900">Qualification Name</TableHead>
                  <TableHead className="py-4 text-xs font-bold text-slate-900">Type</TableHead>
                  <TableHead className="py-4 text-xs font-bold text-slate-900">Issuing Authority</TableHead>
                  <TableHead className="py-4 text-xs font-bold text-slate-900">Issue Date</TableHead>
                  <TableHead className="py-4 text-xs font-bold text-slate-900">Expiry Date</TableHead>
                  <TableHead className="py-4 text-xs font-bold text-slate-900">Status</TableHead>
                  <TableHead className="py-4 text-xs font-bold text-slate-900">Reminders</TableHead>
                  <TableHead className="py-4 text-xs font-bold text-slate-900">Attachments</TableHead>
                  <TableHead className="py-4 text-xs font-bold text-slate-900 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {enrichedQualifications.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={showStaffColumn ? 10 : 9} className="h-64 text-center">
                      <div className="flex flex-col items-center justify-center text-center">
                        <div className="bg-slate-50 p-4 rounded-full mb-4">
                            <Search className="h-8 w-8 text-slate-200" />
                        </div>
                        <p className="text-slate-500 font-medium">No qualifications found.</p>
                        {searchTerm && (
                            <Button 
                                variant="ghost" 
                                size="sm" 
                                className="mt-2 text-blue-600 hover:text-blue-700"
                                onClick={() => setSearchTerm("")}
                            >
                                Clear search
                            </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  enrichedQualifications.map((qualification: EnrichedQualification) => (
                    <TableRow 
                        key={qualification.id} 
                        className="hover:bg-slate-50/50 transition-colors border-slate-100 cursor-pointer"
                        onClick={() => handleViewDetails(qualification)}
                    >
                      {showStaffColumn && (
                        <TableCell className="font-medium text-slate-900">
                          {qualification.person?.name}
                        </TableCell>
                      )}
                      <TableCell className="font-bold text-slate-900">
                        {qualification.name}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize font-medium text-slate-600 bg-slate-50 border-slate-200">
                          {typeof qualification.type === 'object' ? (qualification.type as any).label : qualification.type || "Other"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-500">
                        {qualification.issuingAuthority || "N/A"}
                      </TableCell>
                      <TableCell className="text-slate-900 font-medium whitespace-nowrap">
                        {qualification.issueDate ? formatDate(qualification.issueDate) : "-"}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {qualification.expiryDate ? (
                          <div className="flex flex-col">
                            <span className="text-slate-900 font-medium">{formatDate(qualification.expiryDate)}</span>
                            {qualification.daysUntilExpiry !== null && (
                              <span className="text-[10px] text-slate-400 font-medium">({qualification.daysUntilExpiry} days)</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-400 italic">No expiry</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <QualificationStatusBadge status={qualification.calculatedStatus} />
                      </TableCell>
                      <TableCell>
                        {qualification.requiresRenewal ? (
                          <Badge variant="outline" className="bg-blue-50 text-blue-600 border-blue-100 font-medium">Active</Badge>
                        ) : (
                          <span className="text-xs text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {qualification.attachments && qualification.attachments.length > 0 ? (
                          <div className="flex items-center gap-1.5 text-slate-500">
                            <Paperclip size={14} />
                            <span className="text-xs font-medium">{qualification.attachments.length}</span>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">---</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8 text-slate-400 hover:text-slate-900" 
                            onClick={() => handleViewDetails(qualification)}
                          >
                            <Eye size={16} />
                          </Button>
                          {!isReadOnly && (
                            <>
                              <Can I={ACTIONS.UPDATE} a={RESOURCES.QUALIFICATION}>
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="h-8 w-8 text-slate-400 hover:text-indigo-600" 
                                  onClick={() => handleEditQualification(qualification)}
                                >
                                  <Edit size={16} />
                                </Button>
                              </Can>
                              <Can I={ACTIONS.DELETE} a={RESOURCES.QUALIFICATION}>
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="h-8 w-8 text-slate-400 hover:text-red-600"
                                  onClick={(e) => confirmDelete(qualification.id, e)}
                                >
                                  <Trash2 size={16} />
                                </Button>
                              </Can>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <DeleteQualificationDialog 
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onConfirm={handleDelete}
      />

      <QualificationDrawer 
        open={isDrawerOpen}
        onOpenChange={setIsDrawerOpen}
        qualification={drawerQualification}
        viewOnly={isReadOnly}
      />
    </div>
  );
};
